import { useEffect, useRef } from 'react'
import type { SinkTypeKey } from '../Sinks/SinkRegistry.js'
import { SINK_REGISTRY } from '../Sinks/SinkRegistry.js'
import type { SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { useSelector } from 'react-redux'
import type { State } from '../../../../Flux/Slice'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.tsx'
import { trackVizChart } from '../../../../Services/Telemetry/index.ts'

interface Props {
  sourceId: SourceId
  open: boolean
  onToggle: () => void
  onSelect: (kind: SinkTypeKey) => void
  label?: string
  triggerClassName?: string
  placement?: 'bottom' | 'top'
}

type SinkMenuEntry = {
  kind: SinkTypeKey[]
  title: string
  tooltip?: string
}

const ENTRIES: SinkMenuEntry[] = [
  { kind: ['stackedBar'], title: 'Stacked Chart' },
  { kind: ['rankedBar'], title: 'Ranked Bar (Top Taxa)' },
  { kind: ['pieChart'], title: 'Pie Chart' },
  { kind: ['map'], title: 'Map' },
  { kind: ['treemap'], title: 'Treemap' },
  { kind: ['areaChart'], title: 'Area Chart' },
  { kind: ['lineChart'], title: 'Line Charts' },
  { kind: ['biodiversityAbsoluteEffective'], title: 'Biodiversity: Absolute Effective' },
  { kind: ['biodiversityGiniSimpson'], title: 'Biodiversity: Gini-Simpson' },
  { kind: ['biodiversityRichness'], title: 'Biodiversity: Richness' },
  { kind: ['biodiversityShannon'], title: 'Biodiversity: Shannon' },
  { kind: ['colorLegend'], title: 'Legend' },
  {
    kind: ['biodiversityRichness', 'biodiversityAbsoluteEffective', 'biodiversityGiniSimpson', 'biodiversityShannon'],
    title: 'Biodiversity: All'
  },
  { kind: ['betaDiversity'], title: 'Beta Diversity' },
  { kind: ['overlapHeatmap'], title: 'Overlap Heatmap' },
  { kind: ['upset'], title: 'UpSet Intersections' },
  { kind: ['sharedUnique'], title: 'Shared vs Unique' }
]

export function AddSinkMenu({
  sourceId,
  open,
  onToggle,
  onSelect,
  label = 'Add sink',
  triggerClassName = 'btn btn-sm w-full flex-grow-1',
  placement = 'bottom'
}: Props) {
  const isNatureKPI = useSelector((s: typeof State) => {
    return s.visualization.sources[sourceId]?.shared?.natureKPIs !== undefined
  })

  const rootRef = useRef<HTMLDivElement>(null)

  // Close on outside click / Escape. Without this the menu relied on DaisyUI's `:focus-within`
  // CSS to stay visible — so clicking a row's non-button area moved focus out and the menu
  // vanished WITHOUT selecting anything (the "closes without adding" bug). We keep it open
  // explicitly and only close on a genuine outside click.
  useEffect(() => {
    if (!open) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onToggle()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggle()
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onToggle])

  const renderEntry = (index: number, entry: SinkMenuEntry, isKPI: boolean) => {
    const sinks = entry.kind.map((e) => SINK_REGISTRY[e])
    const enabled = !isKPI || sinks.every((s) => s.capabilities.supportsKPI)
    return (
      <li key={index} className={enabled ? '' : 'opacity-50'}>
        {/* Full-width hitbox: the TooltipTrigger wrapper + button both stretch the whole row, so a
            click anywhere on the row selects. Previously the button was content-width (inline-block
            wrapper) and clicks on the padding missed it entirely. */}
        <TooltipTrigger
          classNames="block w-full"
          tooltip={entry.tooltip}
          disabled={!enabled}
          disabledTooltip={'Sink Type not supported for Nature KPI rendering.'}
        >
          <button
            type="button"
            data-testid={`add-sink-${entry.kind.join('-')}`}
            className={`w-full text-left ${enabled ? '' : 'cursor-not-allowed'}`}
            disabled={!enabled}
            onClick={() =>
              entry.kind.forEach((k) => {
                trackVizChart(k, isKPI)
                onSelect(k)
              })
            }
          >
            {entry.title}
          </button>
        </TooltipTrigger>
      </li>
    )
  }

  return (
    <div ref={rootRef} className={`dropdown dropdown-${placement} w-full ${open ? 'dropdown-open' : ''}`}>
      <button type="button" data-testid="add-sink-button" className={triggerClassName} onClick={onToggle}>
        {label}
      </button>
      {open && (
        <ul className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-64 z-20">
          {ENTRIES.map((entry, index) => renderEntry(index, entry, isNatureKPI))}
        </ul>
      )}
    </div>
  )
}
